import { ensureTransaction } from './data';

/**
 * Transitions
 *
 * These strings must sync with values defined in Flex API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

// When a customer makes a booking to a listing, a transaction is
// created with the initial request-payment transition.
// At this transition a PaymentIntent is created by Marketplace API.
// After this transition, the actual payment must be made on client-side directly to Stripe.
export const TRANSITION_REQUEST_PAYMENT = 'transition/request-payment';

// A customer can also initiate a transaction with an enquiry, and
// then transition that with a request.
export const TRANSITION_ENQUIRE = 'transition/enquire';
export const TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY = 'transition/request-payment-after-enquiry';
export const TRANSITION_MAKE_CUSTOM_OFFER = 'transition/make-custom-offer';

// Pro process transitions
export const TRANSITION_MAKE_CUSTOM_PRO_OFFER = 'transition/make-custom-pro-offer';
export const TRANSITION_ACCEPT_CUSTOM_PRO_OFFER = 'transition/accept-custom-pro-offer';
export const TRANSITION_JOB_DONE_PRO = 'transition/job-done-pro';
export const TRANSITION_JOB_ACCEPTED = 'transition/job-accepted';
export const TRANSITION_SKIP_TO_REVIEWS = 'transition/skip-to-reviews';

// Invoice branch of flex-default-process and flex-light-process (2026-09).
// A customer approved for invoicing accepts the offer without Stripe; the
// branch arrives at state/delivered like the card path.
export const TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE = 'transition/accept-custom-offer-invoice';
export const TRANSITION_JOB_DONE_INVOICE = 'transition/job-done-invoice';
export const TRANSITION_ACCEPT_JOB_INVOICE = 'transition/accept-job-invoice';
export const TRANSITION_JOB_DONE_ACCEPT_INVOICE = 'transition/job-done-accept-invoice';
export const TRANSITION_CANCEL_INVOICE = 'transition/cancel-invoice';

// Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
// Therefore we need to make another transition to Marketplace API,
// to tell that the payment is confirmed.
export const TRANSITION_CONFIRM_PAYMENT = 'transition/confirm-payment';

export const TRANSITION_CONFIRM_PROJECT = 'transition/confirm-project';

// If the payment is not confirmed in the time limit set in transaction process (by default 15min)
// the transaction will expire automatically.
export const TRANSITION_EXPIRE_PAYMENT = 'transition/expire-payment';

// The payment can also be cancelled by the customer from the checkout page
export const TRANSITION_CANCEL_PAYMENT = 'transition/cancel-payment';

// When the provider accepts or declines a transaction from the
// SalePage, it is transitioned with the accept or decline transition.
export const TRANSITION_ACCEPT = 'transition/accept';
export const TRANSITION_DECLINE = 'transition/decline';

// The backend automatically expire the transaction.
export const TRANSITION_EXPIRE = 'transition/expire';

// Admin can also cancel the transition.
export const TRANSITION_CANCEL = 'transition/cancel';

// The backend will mark the transaction completed.
export const TRANSITION_COMPLETE = 'transition/complete';

// The backend will mark the transaction completed.
export const TRANSITION_CANCEL_BY_PROVIDER = 'transition/cancel-by-provider';

// The customer releases the funds to the provider.
export const TRANSITION_RELEASE_FUNDS = 'transition/release-funds';

// Reviews are given through transaction transitions. Review 1 can be
// by provider or customer, and review 2 will be the other party of
// the transaction.
export const TRANSITION_REVIEW_1_BY_PROVIDER = 'transition/review-1-by-provider';
export const TRANSITION_REVIEW_2_BY_PROVIDER = 'transition/review-2-by-provider';
export const TRANSITION_REVIEW_1_BY_CUSTOMER = 'transition/review-1-by-customer';
export const TRANSITION_REVIEW_2_BY_CUSTOMER = 'transition/review-2-by-customer';
export const TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD = 'transition/expire-customer-review-period';
export const TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD = 'transition/expire-provider-review-period';
export const TRANSITION_EXPIRE_REVIEW_PERIOD = 'transition/expire-review-period';
export const TRANSITION_JOB_DONE = 'transition/job-done';
export const TRANSITION_JOB_DONE_CUSTOMER = 'transition/job-done-customer';
export const TRANSITION_JOB_DONE_RELEASE_FUNDS = 'transition/job-done-release-funds';
// Project transaction process transitions

export const TRANSITION_MAKE_PROJECT_BID = 'transition/make-project-bid';
export const TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY =
  'transition/make-project-bid-after-enquiry';
export const TRANSITION_ACCEPT_OFFER = 'transition/accept-offer';
export const TRANSITION_DECLINE_OFFER = 'transition/decline-offer';
export const TRANSITION_EXPIRE_OFFER = 'transition/expire-offer';

// Project version 23 transitions
export const TRANSITION_ACCEPT_APPLICATION = 'transition/accept-application';
export const TRANSITION_SKIP_APPLICATION = 'transition/skip-application';
export const TRANSITION_ACCEPT_AFTER_SKIP = 'transition/accept-after-skip';
export const TRANSITION_EXPIRE_ENQUIRY = 'transition/expire-enquiry';
export const TRANSITION_EXPIRE_APPLICATION = 'transition/expire-application';

/**
 * Actors
 *
 * There are 4 different actors that might initiate transitions:
 */

// Roles of actors that perform transaction transitions
export const TX_TRANSITION_ACTOR_CUSTOMER = 'customer';
export const TX_TRANSITION_ACTOR_PROVIDER = 'provider';
export const TX_TRANSITION_ACTOR_SYSTEM = 'system';
export const TX_TRANSITION_ACTOR_OPERATOR = 'operator';

export const TX_TRANSITION_ACTORS = [
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  TX_TRANSITION_ACTOR_SYSTEM,
  TX_TRANSITION_ACTOR_OPERATOR,
];

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
const STATE_INITIAL = 'initial';
const STATE_ENQUIRY = 'enquiry';
const STATE_PENDING_PAYMENT = 'pending-payment';
const STATE_PAYMENT_EXPIRED = 'payment-expired';
const STATE_PREAUTHORIZED = 'preauthorized';
const STATE_DECLINED = 'declined';
const STATE_ACCEPTED = 'accepted';
const STATE_CANCELED = 'canceled';
const STATE_PROJECT_CONFIRMED = 'project-confirmed';
const STATE_DELIVERED = 'delivered';
const STATE_REVIEWED = 'reviewed';
const STATE_REVIEWED_BY_CUSTOMER = 'reviewed-by-customer';
const STATE_REVIEWED_BY_PROVIDER = 'reviewed-by-provider';
const STATE_JOB_READY = 'job-ready';

// Project transaction process states

const STATE_OFFER_MADE = 'offer-made';
const STATE_OFFER_ACCEPTED = 'offer-accepted';
const STATE_OFFER_DECLINED = 'offer-declined';

// Project version 23 process states
const STATE_APPLICATION_SKIPPED = 'application-skipped';
const STATE_APPLICATION_ACCEPTED = 'application-accepted';
const STATE_APPLICATION_DECLINED = 'application-declined';

// Pro process states
const STATE_CUSTOM_PRO_OFFER_ACCEPTED = 'custom-pro-offer-accepted';
const STATE_JOB_READY_ACCEPTED = 'job-ready-accepted';

// Invoice branch states
const STATE_INVOICE_OFFER_ACCEPTED = 'invoice-offer-accepted';
const STATE_INVOICE_JOB_READY = 'invoice-job-ready';

/**
 * Description of transaction process
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
const stateDescription = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'flex-default-process/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: STATE_INITIAL,

  // States
  states: {
    [STATE_INITIAL]: {
      on: {
        [TRANSITION_ENQUIRE]: STATE_ENQUIRY,
        [TRANSITION_REQUEST_PAYMENT]: STATE_PENDING_PAYMENT,
      },
    },
    [STATE_ENQUIRY]: {
      on: {
        [TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY]: STATE_PENDING_PAYMENT,
        [TRANSITION_MAKE_CUSTOM_OFFER]: STATE_ENQUIRY,
        [TRANSITION_DECLINE_OFFER]: STATE_ENQUIRY,
        [TRANSITION_MAKE_PROJECT_BID]: STATE_OFFER_MADE,
        [TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY]: STATE_OFFER_MADE,
        [TRANSITION_ACCEPT_APPLICATION]: STATE_APPLICATION_ACCEPTED,
        [TRANSITION_EXPIRE_ENQUIRY]: STATE_APPLICATION_DECLINED,
        [TRANSITION_SKIP_APPLICATION]: STATE_APPLICATION_SKIPPED,
        [TRANSITION_MAKE_CUSTOM_PRO_OFFER]: STATE_ENQUIRY,
        [TRANSITION_ACCEPT_CUSTOM_PRO_OFFER]: STATE_CUSTOM_PRO_OFFER_ACCEPTED,
        [TRANSITION_SKIP_TO_REVIEWS]: STATE_JOB_READY_ACCEPTED,
        [TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE]: STATE_INVOICE_OFFER_ACCEPTED,
      },
    },
    [STATE_CUSTOM_PRO_OFFER_ACCEPTED]: {
      on: {
        [TRANSITION_JOB_DONE_PRO]: STATE_JOB_READY,
      },
    },
    [STATE_JOB_READY_ACCEPTED]: {
      on: {
        [TRANSITION_EXPIRE_REVIEW_PERIOD]: STATE_REVIEWED,
        [TRANSITION_REVIEW_1_BY_CUSTOMER]: STATE_REVIEWED_BY_CUSTOMER,
        [TRANSITION_REVIEW_1_BY_PROVIDER]: STATE_REVIEWED_BY_PROVIDER,
      },
    },
    [STATE_INVOICE_OFFER_ACCEPTED]: {
      on: {
        [TRANSITION_JOB_DONE_INVOICE]: STATE_INVOICE_JOB_READY,
        [TRANSITION_JOB_DONE_ACCEPT_INVOICE]: STATE_DELIVERED,
        [TRANSITION_CANCEL_INVOICE]: STATE_CANCELED,
      },
    },
    [STATE_INVOICE_JOB_READY]: {
      on: {
        [TRANSITION_ACCEPT_JOB_INVOICE]: STATE_DELIVERED,
      },
    },
    [STATE_APPLICATION_SKIPPED]: {
      on: {
        [TRANSITION_ACCEPT_AFTER_SKIP]: STATE_APPLICATION_ACCEPTED,
        [TRANSITION_EXPIRE_APPLICATION]: STATE_APPLICATION_DECLINED,
      },
    },
    [STATE_OFFER_MADE]: {
      on: {
        [TRANSITION_ACCEPT_OFFER]: STATE_OFFER_ACCEPTED,
        [TRANSITION_DECLINE_OFFER]: STATE_OFFER_DECLINED,
        [TRANSITION_EXPIRE_OFFER]: STATE_OFFER_DECLINED,
      },
    },

    [STATE_PENDING_PAYMENT]: {
      on: {
        [TRANSITION_EXPIRE_PAYMENT]: STATE_PAYMENT_EXPIRED,
        [TRANSITION_CONFIRM_PAYMENT]: STATE_ACCEPTED,
        [TRANSITION_CONFIRM_PROJECT]: STATE_ACCEPTED,
        [TRANSITION_CANCEL_PAYMENT]: STATE_ENQUIRY,
      },
    },

    [STATE_PAYMENT_EXPIRED]: {},

    [STATE_DECLINED]: {},
    [STATE_PROJECT_CONFIRMED]: {
      on: {
        [TRANSITION_JOB_DONE]: STATE_JOB_READY,
        [TRANSITION_JOB_DONE_CUSTOMER]: STATE_JOB_READY,
        [TRANSITION_JOB_DONE_RELEASE_FUNDS]: STATE_DELIVERED,
      },
    },

    [STATE_JOB_READY]: {
      on: {
        [TRANSITION_RELEASE_FUNDS]: STATE_DELIVERED,
        [TRANSITION_JOB_ACCEPTED]: STATE_JOB_READY_ACCEPTED,
      },
    },
    [STATE_CANCELED]: {},
    [STATE_DELIVERED]: {
      on: {
        [TRANSITION_EXPIRE_REVIEW_PERIOD]: STATE_REVIEWED,
        [TRANSITION_REVIEW_1_BY_CUSTOMER]: STATE_REVIEWED_BY_CUSTOMER,
        [TRANSITION_REVIEW_1_BY_PROVIDER]: STATE_REVIEWED_BY_PROVIDER,
      },
    },

    [STATE_REVIEWED_BY_CUSTOMER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_PROVIDER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED_BY_PROVIDER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_CUSTOMER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED]: { type: 'final' },
    [STATE_OFFER_ACCEPTED]: { type: 'final' },
    [STATE_OFFER_DECLINED]: { type: 'final' },
  },
};

// Note: currently we assume that state description doesn't contain nested states.
const statesFromStateDescription = description => description.states || {};

// Get all the transitions from states object in an array
const getTransitions = states => {
  const stateNames = Object.keys(states);

  const transitionsReducer = (transitionArray, name) => {
    const stateTransitions = states[name] && states[name].on;
    const transitionKeys = stateTransitions ? Object.keys(stateTransitions) : [];
    return [
      ...transitionArray,
      ...transitionKeys.map(key => ({ key, value: stateTransitions[key] })),
    ];
  };

  return stateNames.reduce(transitionsReducer, []);
};

// This is a list of all the transitions that this app should be able to handle.
export const TRANSITIONS = getTransitions(statesFromStateDescription(stateDescription)).map(
  t => t.key
);

// This function returns a function that has given stateDesc in scope chain.
const getTransitionsToStateFn = stateDesc => state =>
  getTransitions(statesFromStateDescription(stateDesc))
    .filter(t => t.value === state)
    .map(t => t.key);

// Get all the transitions that lead to specified state.
const getTransitionsToState = getTransitionsToStateFn(stateDescription);

// This is needed to fetch transactions that need response from provider.
// I.e. transactions which provider needs to accept or decline
export const transitionsToRequested = getTransitionsToState(STATE_PREAUTHORIZED);

/**
 * Helper functions to figure out if transaction is in a specific state.
 * State is based on lastTransition given by transaction object and state description.
 */

export const txLastTransition = tx => ensureTransaction(tx).attributes.lastTransition;

export const txIsEnquired = tx =>
  getTransitionsToState(STATE_ENQUIRY).includes(txLastTransition(tx));

export const txIsProjectOfferMade = tx =>
  getTransitionsToState(STATE_OFFER_MADE).includes(txLastTransition(tx));

export const txIsProjectAccepted = tx =>
  getTransitionsToState(STATE_OFFER_ACCEPTED).includes(txLastTransition(tx));

export const txIsProjectDeclined = tx =>
  getTransitionsToState(STATE_OFFER_DECLINED).includes(txLastTransition(tx));

export const txIsProAccepted = tx =>
  getTransitionsToState(STATE_CUSTOM_PRO_OFFER_ACCEPTED).includes(txLastTransition(tx));

export const txIsProDone = tx =>
  getTransitionsToState(STATE_JOB_READY).includes(txLastTransition(tx));

export const txIsProReadyAccepted = tx =>
  getTransitionsToState(STATE_JOB_READY_ACCEPTED).includes(txLastTransition(tx));

export const txIsInvoiceOfferAccepted = tx =>
  getTransitionsToState(STATE_INVOICE_OFFER_ACCEPTED).includes(txLastTransition(tx));

export const txIsInvoiceJobReady = tx =>
  getTransitionsToState(STATE_INVOICE_JOB_READY).includes(txLastTransition(tx));

export const txIsPaymentPending = tx =>
  getTransitionsToState(STATE_PENDING_PAYMENT).includes(txLastTransition(tx));

export const txIsPaymentExpired = tx =>
  getTransitionsToState(STATE_PAYMENT_EXPIRED).includes(txLastTransition(tx));

// Note: state name used in Marketplace API docs (and here) is actually preauthorized
// However, word "requested" is used in many places so that we decided to keep it.
export const txIsRequested = tx =>
  getTransitionsToState(STATE_PREAUTHORIZED).includes(txLastTransition(tx));
export const txIsAccepted = tx =>
  getTransitionsToState(STATE_ACCEPTED).includes(txLastTransition(tx));

export const txIsDeclined = tx =>
  getTransitionsToState(STATE_DECLINED).includes(txLastTransition(tx));

export const txIsCanceled = tx =>
  getTransitionsToState(STATE_CANCELED).includes(txLastTransition(tx));

export const txIsProjectConfirmed = tx =>
  getTransitionsToState(STATE_PROJECT_CONFIRMED).includes(txLastTransition(tx));

export const txIsDelivered = tx =>
  getTransitionsToState(STATE_DELIVERED).includes(txLastTransition(tx));

export const txIsJobReady = tx =>
  getTransitionsToState(STATE_JOB_READY).includes(txLastTransition(tx));

const firstReviewTransitions = [
  ...getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER),
  ...getTransitionsToState(STATE_REVIEWED_BY_PROVIDER),
];
export const txIsInFirstReview = tx => firstReviewTransitions.includes(txLastTransition(tx));

export const txIsInFirstReviewBy = (tx, isCustomer) =>
  isCustomer
    ? getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(txLastTransition(tx))
    : getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(txLastTransition(tx));

export const txIsReviewed = tx =>
  getTransitionsToState(STATE_REVIEWED).includes(txLastTransition(tx));

/**
 * Helper functions to figure out if transaction has passed a given state.
 * This is based on transitions history given by transaction object.
 */

const txTransitions = tx => ensureTransaction(tx).attributes.transitions || [];
const hasPassedTransition = (transitionName, tx) =>
  !!txTransitions(tx).find(t => t.transition === transitionName);

export const hasPassedStateFn = state => tx =>
  getTransitionsToState(state).filter(t => hasPassedTransition(t, tx)).length > 0;

export const txHasBeenAccepted = hasPassedStateFn(STATE_ACCEPTED);
export const txHasBeenDelivered = hasPassedStateFn(STATE_DELIVERED);

// Legacy invoice transactions live on their own process; new ones are a branch
// inside default/light. Rendering decisions use txUsesInvoicePath, never the
// customer's publicData flags, so flipping a flag cannot change an in-flight
// transaction.
export const txIsLegacyProProcess = tx =>
  ensureTransaction(tx).attributes.processName === 'flex-pro-process';

export const txUsesInvoicePath = tx =>
  txIsLegacyProProcess(tx) || hasPassedTransition(TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE, tx);

/**
 * Other transaction related utility functions
 */

export const transitionIsReviewed = transition =>
  getTransitionsToState(STATE_REVIEWED).includes(transition);

export const transitionIsFirstReviewedBy = (transition, isCustomer) =>
  isCustomer
    ? getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(transition)
    : getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(transition);

export const getReview1Transition = isCustomer =>
  isCustomer ? TRANSITION_REVIEW_1_BY_CUSTOMER : TRANSITION_REVIEW_1_BY_PROVIDER;

export const getReview2Transition = isCustomer =>
  isCustomer ? TRANSITION_REVIEW_2_BY_CUSTOMER : TRANSITION_REVIEW_2_BY_PROVIDER;

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    TRANSITION_ACCEPT,
    TRANSITION_CANCEL,
    TRANSITION_CANCEL_PAYMENT,
    TRANSITION_COMPLETE,
    TRANSITION_RELEASE_FUNDS,
    TRANSITION_CONFIRM_PAYMENT,
    TRANSITION_DECLINE,
    TRANSITION_EXPIRE,
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
    TRANSITION_ACCEPT_OFFER,
    TRANSITION_DECLINE_OFFER,
    TRANSITION_EXPIRE_OFFER,
    TRANSITION_CONFIRM_PROJECT,
    TRANSITION_MAKE_PROJECT_BID,
    TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY,
    TRANSITION_JOB_DONE,
    TRANSITION_JOB_DONE_RELEASE_FUNDS,
    TRANSITION_MAKE_CUSTOM_OFFER,
    TRANSITION_MAKE_CUSTOM_PRO_OFFER,
    TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
    TRANSITION_JOB_DONE_PRO,
    TRANSITION_JOB_ACCEPTED,
    TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
    TRANSITION_JOB_DONE_INVOICE,
    TRANSITION_ACCEPT_JOB_INVOICE,
    TRANSITION_JOB_DONE_ACCEPT_INVOICE,
    TRANSITION_CANCEL_INVOICE,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [TRANSITION_REVIEW_1_BY_CUSTOMER, TRANSITION_REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [TRANSITION_REVIEW_1_BY_PROVIDER, TRANSITION_REVIEW_2_BY_PROVIDER].includes(transition);
};

export const getUserTxRole = (currentUserId, transaction) => {
  const tx = ensureTransaction(transaction);
  const customer = tx.customer;
  if (currentUserId && currentUserId.uuid && tx.id && customer.id) {
    // user can be either customer or provider
    return currentUserId.uuid === customer.id.uuid
      ? TX_TRANSITION_ACTOR_CUSTOMER
      : TX_TRANSITION_ACTOR_PROVIDER;
  } else {
    throw new Error(`Parameters for "userIsCustomer" function were wrong.
      currentUserId: ${currentUserId}, transaction: ${transaction}`);
  }
};

export const txRoleIsProvider = userRole => userRole === TX_TRANSITION_ACTOR_PROVIDER;
export const txRoleIsCustomer = userRole => userRole === TX_TRANSITION_ACTOR_CUSTOMER;

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    TRANSITION_REQUEST_PAYMENT,
    TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
    TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  ].includes(transition);
};
